import { include, reverse } from 'named-urls';

/** All authenticated endpoints in the base API */
export const BASE_API_ROUTES = {
  contacts: include('/contacts', {
    ':contactId': include(':contactId', {
      get: '',
      clients: include('clients', {
        ':clientId': include(':clientId', {
          'files-uploaded': 'files-uploaded',
          ['presigned-url']: 'presigned-url',
          intake: 'intake',
          schedule: 'schedule',
          info: 'info',
          'care-team': 'care-team',
          'care-plan': include('care-plan', {
            goals: 'goals',
            'visit-frequency': 'visit-frequency',
            diagnoses: 'diagnoses',
            'medical-diagnoses': 'medical-diagnoses',
            'additional-goals': 'additional-goals',
          }),
          appointments: include('appointments', {
            upcoming: 'upcoming',
            'cancellations-data': 'cancellations-data',
            ':appointmentId': include(':appointmentId', {
              get: '',
              reschedule: 'reschedule',
              cancel: 'cancel',
              'is-late-cancel': 'is-late-cancel',
            }),
          }),
          notes: include('notes', {
            all: '',
            latest: 'latest',
            ':noteId': include(':noteId', {
              base: '',
              pdf: 'pdf',
            }),
          }),
          assignments: include('assignments', {
            all: '',
            ':assignmentId': include(':assignmentId', {
              get: '',
              complete: 'complete',
            }),
          }),
          billing: include('billing', {
            info: 'info',
          }),
          claims: include('claims', {
            all: '',
            ':claimId': ':claimId',
          }),
          payments: include('payments', {
            all: '',
            ':paymentId': ':paymentId',
          }),
          'payment-methods': include('payment-methods', {
            all: '',
          }),
          'release-of-information': 'roi',
          charges: include('charges', {
            ':chargeId': ':chargeId',
          }),
        }),
      }),
    }),
    clients: 'clients',
  }),
  therapists: include('therapists', {
    all: '',
    ':therapistId': ':therapistId',
  }),

  clients: include('clients', {
    ':clientId': include(':clientId', {
      ['presigned-url']: 'presigned-url',
    }),
  }),
};

/** All anonymous endpoints in the base API */
export const BASE_ANONYMOUS_API_ROUTES = {
  appointments: include('appointments', {
    'rescheduling-available-times': 'rescheduling-available-times',
    'scheduling-available-times': 'scheduling-available-times',
    'available-times': 'available-times',
    'calendar-availability': 'calendar-availability',
    'eval-available-times': 'eval-available-times',
    clients: include('clients', {
      ':clientId': include(':clientId', {
        recurring: 'recurring',
      }),
    }),
    v3: include('v3', {
      'scheduling-available-times': 'scheduling-available-times',
    }),
    v4: include('v4', {
      'scheduling-available-times': 'scheduling-available-times',
    }),
    consultations: include('consultations', {
      'available-times': 'available-times',
    }),
    reservation: include('reservation', {
      ':rid': ':rid',
    }),
  }),

  therapists: include('therapists', {
    signup: 'signup',
  }),

  clients: include('clients', {
    ':clientId': include(':clientId', {
      'pending-reschedules': include('pending-reschedules', {
        ':rescheduleId': include(':rescheduleId', {
          get: '',
          reschedule: 'reschedule',
        }),
      }),
      'pending-schedules': include('pending-schedules', {
        ':scheduleId': include(':scheduleId', {
          get: '',
          schedule: 'schedule',
        }),
      }),
      ['aggregated-session-analysis']: 'aggregated-language-analysis',
    }),

    'contracted-business': 'contracted-business',
  }),

  contacts: include('/contacts', {
    ':contactId': include(':contactId', {
      get: '',
      clients: include('clients', {
        ':clientId': include(':clientId', {
          ['aggregated-session-analysis']: 'aggregated-language-analysis',
        }),
      }),
    }),
  }),

  billing: include('billing', {
    appointment: include('appointment', {
      ':type': include(':type', {
        price: 'price',
      }),
    }),
    stripe: include('stripe', {
      account: include('account', {
        ':state': ':state',
      }),
    }),
    signup: 'schedule-signup-evaluation',
  }),

  eligibility: include('/eligibility', {
    check: 'check',
  }),

  'pdf-generator': 'pdf-generator',

  signup: include('/signup', {
    lead: include('lead', {
      [':leadID']: ':leadID',
    }),
  }),
};

/** Utility function to quickly extract the URL string from the API routes object. To be used for API calls */
export const getBaseAPIRoute: typeof reverse = (route, params) => {
  const result = reverse(route, params);

  /** @note(juan): this is needed because named-urls always adds a / at the end of URLs. This is probably because its meant
   * to be used with react-router but we use it directly for API calls. We just remove this ending /
   */
  if (result.endsWith('/')) {
    return result.substring(0, result.length - 1);
  }
  return result;
};
